import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/app/store";
import { toast } from "top-brains-ui";
import {
  updateDownloadProgress,
  completeVideoDownload,
  finishCurrentAndStartNext,
  markTaskAsFailed,
} from "@src/app/videoDownloadSlice";
import {
  getResourceUrl,
  electronDownloadFile,
  isElectronMediaRuntime,
  cancelElectronDownload,
  buildForwardedForHeader,
} from "@src/services/mediaRuntime";
import { storageService } from "@src/services/StorageService";

// Global cache for resolved video URLs
export const checkedVideoUrlsCache = new Map<string, string | null>();

export let activeDownloadAbortController: AbortController | null = null;
export const cancelledTaskKeys = new Set<string>();

export const clearVideoCache = (
  nodeId?: string | null,
  targetId?: string | null,
) => {
  if (!nodeId && !targetId) {
    checkedVideoUrlsCache.clear();
    cancelledTaskKeys.clear();
    return;
  }
  for (const key of checkedVideoUrlsCache.keys()) {
    if (
      (nodeId && key.includes(nodeId)) ||
      (targetId && key.includes(targetId))
    ) {
      checkedVideoUrlsCache.delete(key);
    }
  }
};

export const cancelActiveDownload = async (
  nodeId?: string | null,
  targetId?: string | null,
  docId?: string | null,
) => {
  if (nodeId) cancelledTaskKeys.add(nodeId);
  if (targetId) cancelledTaskKeys.add(targetId);
  if (docId) cancelledTaskKeys.add(docId);

  // Immediately abort native Electron download stream if active
  try {
    await cancelElectronDownload();
  } catch (e) {
    // ignore
  }

  if (activeDownloadAbortController) {
    try {
      activeDownloadAbortController.abort();
    } catch (e) {
      // ignore
    }
    activeDownloadAbortController = null;
  }

  clearVideoCache(nodeId, targetId);
  if (docId) clearVideoCache(docId);

  const envFolder = import.meta.env.VITE_EXTRACTED_RESOURCE_FOLDER;
  const extractionPath = envFolder ? [envFolder] : [];

  const deleteEncFiles = async () => {
    const toDelete = [
      targetId ? `${targetId}.enc` : null,
      nodeId && nodeId !== targetId ? `${nodeId}.enc` : null,
      docId && docId !== targetId && docId !== nodeId ? `${docId}.enc` : null,
    ].filter(Boolean) as string[];

    for (const fileName of toDelete) {
      try {
        await storageService.deletePath([...extractionPath, fileName]);
      } catch (e) {
        console.warn("Failed to delete cancelled file:", e);
      }
    }
  };

  await deleteEncFiles();

  // Retry deletion with delays in case Electron process / OS file handle was locked during download abort
  setTimeout(deleteEncFiles, 500);
  setTimeout(deleteEncFiles, 1500);
};

const GlobalVideoDownloader: React.FC = () => {
  const dispatch = useDispatch();
  const downloadState = useSelector((state: RootState) => state?.videoDownload);
  const currentTask = downloadState?.currentTask ?? null;

  const activeTaskRef = useRef<string | null>(null);
  const lastToastTimeRef = useRef<number>(0);

  const isVideoDownloading = useSelector((state: RootState) => {
    const vd = state?.videoDownload;
    if (!vd) return false;

    const hasNoQueuedTasks = !vd.downloadQueue || vd.downloadQueue.length === 0;
    const isCompleted = vd.downloadStage === "completed";
    const isFailed =
      vd.downloadStage === "failed" ||
      (vd.downloadingNodeId &&
        Boolean(vd.failedTasks?.[vd.downloadingNodeId])) ||
      (vd.currentTask?.nodeId &&
        Boolean(vd.failedTasks?.[vd.currentTask.nodeId]));

    if ((isCompleted || isFailed) && hasNoQueuedTasks) {
      return false;
    }

    return Boolean(vd.isDownloading || vd.currentTask);
  });

  // Block keyboard shortcuts (F5, Ctrl+R, Cmd+R, etc.) and beforeunload while downloading
  useEffect(() => {
    if (!isVideoDownloading) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isF5 = e.key === "F5" || e.code === "F5";
      const isCtrlOrCmdR =
        (e.ctrlKey || e.metaKey) &&
        (e.key === "r" || e.key === "R" || e.code === "KeyR");

      if (isF5 || isCtrlOrCmdR) {
        e.preventDefault();
        e.stopPropagation();

        const now = Date.now();
        if (now - lastToastTimeRef.current > 2500) {
          lastToastTimeRef.current = now;
          toast.warning(
            "Reload is disabled while video download is in progress.",
          );
        }
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isVideoDownloading]);

  useEffect(() => {
    if (!currentTask) {
      activeTaskRef.current = null;
      return;
    }

    const taskKey = `${currentTask.nodeId}-${currentTask.targetId}-${currentTask.timestamp}`;
    if (activeTaskRef.current === taskKey) return;
    activeTaskRef.current = taskKey;

    const executeDownload = async () => {
      const { nodeId, targetId, accessToken, nodeName, selectedTab } =
        currentTask;
      const taskCacheKey = `${nodeId}-${selectedTab || "Video"}-${targetId}`;
      checkedVideoUrlsCache.delete(taskCacheKey);
      const baseUrl = import.meta.env.VITE_TEST_FRESHERS_BASE_URL;
      const envFolder = import.meta.env.VITE_EXTRACTED_RESOURCE_FOLDER;
      const extractionPath = envFolder ? [envFolder] : [];
      const encFileName = `${targetId}.enc`;
      const metadataUrl = `${baseUrl}video-load/load-ai-url?resourceId=${targetId}&artifact=video`;

      const isCancelled = () => {
        return (
          cancelledTaskKeys.has(nodeId) ||
          (targetId && cancelledTaskKeys.has(targetId)) ||
          activeTaskRef.current !== taskKey
        );
      };

      const cleanUpOnCancel = async () => {
        try {
          await storageService.deletePath([...extractionPath, encFileName]);
          if (nodeId && nodeId !== targetId) {
            await storageService.deletePath([
              ...extractionPath,
              `${nodeId}.enc`,
            ]);
          }
        } catch (e) {
          // ignore
        }
        clearVideoCache(nodeId, targetId);
      };

      const controller = new AbortController();
      activeDownloadAbortController = controller;

      try {
        if (isCancelled()) {
          await cleanUpOnCancel();
          return;
        }

        dispatch(
          updateDownloadProgress({
            nodeId,
            targetId,
            progress: 0,
            downloadedBytes: 0,
            totalBytes: 0,
            stage: "connecting",
            isExtracting: false,
          }),
        );

        const timeoutId = setTimeout(() => controller.abort(), 20000);
        let xForwardedFor = "";
        if (
          typeof window !== "undefined" &&
          (window as any).electronApp &&
          (window as any).electronApp.getIPInfo
        ) {
          try {
            const ipInfo = await (window as any).electronApp.getIPInfo();
            // Send every local IP so the backend can CIDR-match the real
            // physical NIC, not just the first (possibly virtual) adapter.
            xForwardedFor = buildForwardedForHeader(ipInfo);
          } catch (e) {
            console.error("Failed to get IP info", e);
          }
        }

        const headers: any = {
          Authorization: `Bearer ${accessToken}`,
        };
        if (xForwardedFor) {
          headers["X-Forwarded-For"] = xForwardedFor;
        }
        const metadataResponse = await fetch(metadataUrl, {
          method: "GET",
          headers,
          signal: controller.signal,
        }).finally(() => clearTimeout(timeoutId));

        if (isCancelled()) {
          await cleanUpOnCancel();
          return;
        }

        if (!metadataResponse.ok) {
          throw new Error("Failed to fetch direct resource URL");
        }

        const metadata = await metadataResponse.json();
        if (isCancelled()) {
          await cleanUpOnCancel();
          return;
        }

        const files = metadata?.response?.files || [];
        const file =
          files.find(
            (item: { artifact?: string; url?: string }) =>
              item?.artifact === "video",
          ) || files[0];
        const wanUrl = file?.url;
        const lanUrl = file?.lanUrl as string | undefined;
        const campusName = (file?.campusName ||
          metadata?.response?.campusName) as string | undefined;
        const lanEnabled = Boolean(metadata?.response?.lanEnabled);

        if (!wanUrl) {
          throw new Error("Direct resource URL not found in response");
        }
        // Size of the authoritative copy (main storage). Used to reject a
        // truncated or stale copy on the campus server, and for progress.
        const metadataFileSize = Number(file?.sizeBytes || file?.size || 0);
        // ── LAN-first strategy ──
        // When the backend detects the student is on a registered campus LAN,
        // it returns a lanUrl pointing at /stream-lan which proxies the .enc
        // from the campus SFTP server over the fast local network. We probe
        // the lanUrl with a short HEAD request; if the campus server responds
        // in time, we download over LAN. Otherwise we fall back to the WAN URL.
        let directUrl = wanUrl;
        if (lanUrl && lanEnabled) {
          console.log(
            `[Get Direct Resource] LAN delivery available (campus: ${campusName || "unknown"}). Probing: ${lanUrl}`,
          );
          const lanProbeController = new AbortController();
          const lanProbeTimeout = setTimeout(
            () => lanProbeController.abort(),
            5000,
          );
          try {
            const lanProbeHeaders: Record<string, string> = {
              Authorization: `Bearer ${accessToken}`,
            };
            // Include X-Forwarded-For so the /stream-lan endpoint can resolve
            // the campus server from the client's real LAN IP.
            if (xForwardedFor) {
              lanProbeHeaders["X-Forwarded-For"] = xForwardedFor;
            }
            const lanProbe = await fetch(lanUrl, {
              method: "HEAD",
              signal: lanProbeController.signal,
              headers: lanProbeHeaders,
            });
            const lanFileSize = Number(
              lanProbe.headers.get("content-length") || 0,
            );
            const lanSizeMismatch =
              lanProbe.ok &&
              metadataFileSize > 0 &&
              lanFileSize > 0 &&
              lanFileSize !== metadataFileSize;
            if (lanSizeMismatch) {
              // Campus copy is incomplete or out of date (e.g. an interrupted
              // sync) — downloading it would leave an unplayable video.
              console.warn(
                `[Get Direct Resource] ⚠️ Campus copy size ${lanFileSize} != expected ${metadataFileSize}. Falling back to WAN.`,
              );
            } else if (lanProbe.ok || lanProbe.status === 405) {
              // 405 = endpoint exists but doesn't support HEAD — still reachable
              directUrl = lanUrl;
              console.log(
                `[Get Direct Resource] ✅ LAN probe succeeded (${lanProbe.status}). Streaming from campus "${campusName}" over LAN.`,
              );
            } else {
              console.warn(
                `[Get Direct Resource] ⚠️ LAN probe returned ${lanProbe.status}. Falling back to WAN.`,
              );
            }
          } catch (lanErr) {
            console.warn(
              `[Get Direct Resource] ⚠️ LAN probe failed (${lanErr instanceof Error ? lanErr.message : lanErr}). Falling back to WAN.`,
            );
          } finally {
            clearTimeout(lanProbeTimeout);
          }
        } else if (lanUrl) {
          console.log(
            `[Get Direct Resource] lanUrl present but lanEnabled=false. Using WAN URL.`,
          );
        }
        const isLanDownload = lanUrl && directUrl === lanUrl;

        console.log(
          `[Get Direct Resource] Calling electronDownloadFile for: ${directUrl} (expectedSize: ${metadataFileSize}, mode: ${isLanDownload ? `LAN [${campusName}]` : "WAN"})`,
        );
        // ── ELECTRON PATH: Native HTTP download ──
        if (isElectronMediaRuntime() && window.electronApp?.downloadFile) {
          if (isCancelled()) {
            await cleanUpOnCancel();
            return;
          }

          dispatch(
            updateDownloadProgress({
              nodeId,
              targetId,
              progress: 0,
              downloadedBytes: 0,
              totalBytes: metadataFileSize,
              stage: "downloading",
            }),
          );

          let activeDownloadAttempt = 0;

          const downloadFrom = async (url: string) => {
            // Each attempt gets a unique ID so callbacks from an older
            // LAN/WAN attempt cannot update the current download's progress.
            const attemptId = ++activeDownloadAttempt;
            let lastDispatchTime = 0;

            try {
              return await electronDownloadFile(
                url,
                { Authorization: `Bearer ${accessToken}` },
                extractionPath,
                encFileName,
                (progress) => {
                  if (isCancelled()) return;

                  // Ignore progress events from a previous download attempt.
                  if (attemptId !== activeDownloadAttempt) return;

                  // 1. Validate the target encrypted filename.
                  if (progress.fileName && progress.fileName !== encFileName) {
                    return;
                  }

                  // 2. Reject progress events with a mismatched total size.
                  if (
                    metadataFileSize > 0 &&
                    progress?.totalBytes > 0 &&
                    Math.abs(progress?.totalBytes - metadataFileSize) > 4096
                  ) {
                    return;
                  }

                  // 3. Reject downloaded byte counts exceeding expected size.
                  if (
                    metadataFileSize > 0 &&
                    progress?.downloadedBytes > metadataFileSize + 4096
                  ) {
                    return;
                  }

                  // 4. Prefer metadata size when available.
                  const effectiveTotalBytes =
                    metadataFileSize > 0
                      ? metadataFileSize
                      : progress.totalBytes || 0;

                  // 5. Prevent downloaded bytes from exceeding total bytes.
                  const effectiveDownloadedBytes = Math.min(
                    progress.downloadedBytes || 0,
                    effectiveTotalBytes > 0 ? effectiveTotalBytes : Infinity,
                  );

                  // 6. Keep percentage within the 0–100 range.
                  const effectivePercent = Math.min(
                    100,
                    Math.max(0, progress.percent || 0),
                  );

                  // 7. Throttle Redux updates to approximately every 150 ms,
                  // while always allowing completion and 100% updates.
                  const now = Date.now();

                  if (
                    progress.stage === "completed" ||
                    effectivePercent >= 100 ||
                    now - lastDispatchTime >= 150
                  ) {
                    lastDispatchTime = now;

                    dispatch(
                      updateDownloadProgress({
                        nodeId,
                        targetId,
                        progress: effectivePercent,
                        downloadedBytes: effectiveDownloadedBytes,
                        totalBytes: effectiveTotalBytes,
                        stage:
                          progress.stage === "completed"
                            ? "completed"
                            : "downloading",
                      }),
                    );
                  }
                },
                metadataFileSize > 0 ? metadataFileSize : undefined,
              );
            } catch (e: unknown) {
              return {
                success: false,
                error: e instanceof Error ? e.message : String(e),
              };
            }
          };

          // First attempt: use the direct URL (LAN when applicable).
          let result = await downloadFrom(directUrl);

          // If LAN download fails, retry once using the WAN URL.
          if (isLanDownload && (!result || !result.success)) {
            console.warn(
              `[Get Direct Resource] ⚠️ LAN download failed (${
                result?.error || "unknown error"
              }). Retrying over WAN.`,
            );

            // Invalidate the previous attempt so its late progress
            // callbacks cannot overwrite the WAN attempt's progress.
            activeDownloadAttempt++;

            // Reset progress before starting the WAN retry.
            dispatch(
              updateDownloadProgress({
                nodeId,
                targetId,
                progress: 0,
                downloadedBytes: 0,
                totalBytes: metadataFileSize > 0 ? metadataFileSize : 0,
                stage: "downloading",
              }),
            );

            // Second attempt: use WAN. Errors are handled by downloadFrom.
            result = await downloadFrom(wanUrl);
          }

          if (isCancelled()) {
            await cleanUpOnCancel();
            return;
          }

          if (!result || !result.success) {
            throw new Error(
              `Electron download failed: ${result?.error || "Unknown error"}`,
            );
          }

          const newUrl = getResourceUrl(extractionPath, encFileName);
          checkedVideoUrlsCache.set(taskCacheKey, newUrl);

          dispatch(completeVideoDownload());
          toast.success(`Downloaded: ${nodeName}`);

          await new Promise((resolve) => setTimeout(resolve, 1500));
          dispatch(finishCurrentAndStartNext());
          return;
        }

        // ── BROWSER FALLBACK ──
        if (isCancelled()) {
          await cleanUpOnCancel();
          return;
        }

        dispatch(
          updateDownloadProgress({
            nodeId,
            targetId,
            progress: 0,
            stage: "downloading",
          }),
        );

        const response = await fetch(directUrl, {
          method: "GET",
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: controller.signal,
        });

        if (isCancelled()) {
          await cleanUpOnCancel();
          return;
        }

        if (!response.ok) {
          throw new Error("Failed to download direct resource");
        }

        const contentLengthHeader = response.headers.get("content-length");
        const contentLength = contentLengthHeader
          ? Number(contentLengthHeader)
          : metadataFileSize;

        if (!response.body) {
          const blob = await response.blob();
          if (isCancelled()) {
            await cleanUpOnCancel();
            return;
          }
          await storageService.writeFile(extractionPath, encFileName, blob);
        } else {
          const reader = response.body.getReader();
          const chunks: Uint8Array[] = [];
          let receivedLength = 0;

          let lastDispatchTime = 0;
          while (true) {
            if (isCancelled()) {
              try {
                await reader.cancel();
              } catch (e) {}
              await cleanUpOnCancel();
              return;
            }

            const { done, value } = await reader.read();
            if (done) break;

            chunks.push(value);
            receivedLength += value.length;

            const progress =
              contentLength > 0
                ? Math.round((receivedLength / contentLength) * 100)
                : 50;

            if (isCancelled()) {
              try {
                await reader.cancel();
              } catch (e) {}
              await cleanUpOnCancel();
              return;
            }

            const now = Date.now();
            if (progress >= 100 || now - lastDispatchTime >= 150) {
              lastDispatchTime = now;
              dispatch(
                updateDownloadProgress({
                  nodeId,
                  targetId,
                  progress,
                  downloadedBytes: receivedLength,
                  totalBytes: contentLength,
                  stage: "downloading",
                }),
              );
            }
          }

          if (isCancelled()) {
            await cleanUpOnCancel();
            return;
          }

          const encBytes = new Uint8Array(receivedLength);
          let position = 0;
          for (const chunk of chunks) {
            encBytes.set(chunk, position);
            position += chunk.length;
          }

          await storageService.writeFile(extractionPath, encFileName, encBytes);
        }

        if (isCancelled()) {
          await cleanUpOnCancel();
          return;
        }

        const newUrl = getResourceUrl(extractionPath, encFileName);
        checkedVideoUrlsCache.set(taskCacheKey, newUrl);

        dispatch(completeVideoDownload());
        toast.success(`Downloaded: ${nodeName}`);

        await new Promise((resolve) => setTimeout(resolve, 1500));
        dispatch(finishCurrentAndStartNext());
      } catch (err: any) {
        if (isCancelled()) {
          await cleanUpOnCancel();
          return;
        }

        console.error("[Global Video Downloader] Failure:", err);
        const isTimeout =
          err?.name === "AbortError" || err?.message?.includes("aborted");
        const errorMsg = isTimeout
          ? "Connection timed out while fetching video. Please check your network connection."
          : err?.message ||
            "Failed to download video. Please check your network connection.";
        dispatch(
          markTaskAsFailed({
            nodeId: currentTask.nodeId,
            nodeName: currentTask.nodeName,
            targetId: currentTask.targetId,
            errorMessage: errorMsg,
          }),
        );
        toast.error(`Download failed (${nodeName}): ${errorMsg}`);
        dispatch(finishCurrentAndStartNext());
      } finally {
        if (activeDownloadAbortController === controller) {
          activeDownloadAbortController = null;
        }
      }
    };

    executeDownload();
  }, [currentTask, dispatch]);

  return null;
};

class DownloaderErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.warn(
      "[GlobalVideoDownloader ErrorBoundary]: Caught error",
      error,
      errorInfo,
    );
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

const SafeGlobalVideoDownloader: React.FC = () => (
  <DownloaderErrorBoundary>
    <GlobalVideoDownloader />
  </DownloaderErrorBoundary>
);

export default SafeGlobalVideoDownloader;
